import {
  Alert,
  Link,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { useAtom } from 'jotai';
import { useMemo, useState } from 'react';
import { Link as routerLink } from 'react-router';

import { SHOW_TICKET_ALERT_STORAGE_KEY } from '@/constants/common';
import { orgNotificationSettingsAtom } from '@/features/NotificationSettings/NotificationSettings.store';
import NotificationSettingsAlertDialog from '@/features/NotificationSettingsAlertDialog';
import { openTicketsCountAtom } from '@/pages/Tickets/Tickets.store';

export const useTopbar = () => {
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up('lg'));
  const [orgNotificationSettings] = useAtom(orgNotificationSettingsAtom);
  const [openTicketsCount] = useAtom(openTicketsCountAtom);
  // count the user last dismissed the alert at; alert shows again when count differs
  const [dismissedCount, setDismissedCount] = useState(() =>
    localStorage.getItem(SHOW_TICKET_ALERT_STORAGE_KEY)
  );
  const showTicketAlert = `${openTicketsCount}` !== dismissedCount;
  const [open, setOpen] = useState(false);

  //-----------------------------------------------------------------
  // remember the user's choice to hide the alert
  const handleCloseAlert = () => {
    setDismissedCount(`${openTicketsCount}`);
    localStorage.setItem(SHOW_TICKET_ALERT_STORAGE_KEY, `${openTicketsCount}`);
  };

  //-----------------------------------------------------------------
  const NotificationAlert = useMemo(() => {
    if (
      Object.values(orgNotificationSettings).some((value) => !value) &&
      matches
    ) {
      return (
        <>
          <Alert
            color="error"
            severity="error"
            sx={{ cursor: 'pointer', py: 0 }}
            onClick={() => {
              setOpen(true);
            }}
          >
            There are issues with organization notification settings!
          </Alert>
          <NotificationSettingsAlertDialog open={open} setOpen={setOpen} />
        </>
      );
    }
    return null;
  }, [matches, open, orgNotificationSettings]);

  const TicketAlert = useMemo(() => {
    if (openTicketsCount > 0 && showTicketAlert && matches) {
      return (
        <Alert
          color="warning"
          severity="warning"
          icon={false}
          sx={{ py: 0 }}
          onClose={handleCloseAlert}
        >
          <Link
            component={routerLink}
            to='/tickets?t=all|C&q={"status_name":"Open"}'
            underline="none"
            variant="body1"
            color="inherit"
          >
            You have{' '}
            <Typography
              sx={{ fontWeight: 'bold' }}
              color="textPrimary"
              component="span"
            >
              {openTicketsCount}
            </Typography>{' '}
            open tickets
          </Link>
        </Alert>
      );
    }
    return null;
  }, [openTicketsCount, showTicketAlert, matches]);

  return {
    NotificationAlert,
    TicketAlert,
    openTickesCount: openTicketsCount
  };
};

export default useTopbar;
